import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
// Service unifié DailySales (partagé avec fixed)
import {
  DailySalesService,
  ProductSaleEntry,
  ProductSale,
  DailySalesPayload,
  PendingEvent,
} from '../../../services/daily-sales.service';
// Service prédictions pour les recommandations événements
import {
  PredictionsService,
  PredictedProduct,
} from '../../../services/predictions.service';
import { ProductsService } from '../../../services/product.service';
import { Product } from '../../../models/product.model';
import { EventService } from '../../../services/event.service';
import { LogService } from '../../../services/log.service';

@Component({
  selector: 'app-sales-entry-itinerant',
  imports: [CommonModule, FormsModule],
  templateUrl: './sales-entry-itinerant.component.html',
  styleUrls: ['./sales-entry-itinerant.component.css'],
})
export class SalesEntryItinerantComponent implements OnInit {
  private log = inject(LogService);
  private cdr = inject(ChangeDetectorRef);

  step: 'select-event' | 'enter-sales' | 'context' | 'summary' = 'select-event';

  // ── Étape 1: Sélection événement ──
  pendingEvents: PendingEvent[] = [];
  selectedEvent: PendingEvent | null = null;

  // ── Étape 2: Saisie produits (même interface que fixed) ──
  products: Product[] = [];
  productSales: ProductSale[] = [];

  // ── Étape 3: Contexte (identique au fixed + durée) ──
  contextFlags = {
    ruptureStock: false,
    unusualAffluence: false,
    weatherImpact: false,
    localEvent: false,
    technicalIssue: false,
    differentLocation: false,
  };
  notes: string = '';
  actualAttendance: string = '';
  actualWeather: string = '';
  actualDuration: number = 0;

  // ── Prédictions (recommandations IA événement) ──
  predictions: Map<string, PredictedProduct> = new Map();
  predictionsLoaded: boolean = false;

  // ── État ──
  loading: boolean = false;
  error: string = '';
  successMessage: string = '';
  isDraft: boolean = false;

  // ── Résultat post-soumission (Step 4) ──
  submittedAccuracy: number = 0;
  learningUpdated: boolean = false;
  aiFeedbackMessages: { icon: string; message: string; type: string }[] = [];

  // QueryParams (navigation depuis dashboard)
  private queryEventId: string | null = null;
  private directNav = false;
  editSaleId: string | null = null; // ID du DailySales à corriger

  constructor(
    private dailySalesService: DailySalesService,
    private predictionsService: PredictionsService,
    private productsService: ProductsService,
    private eventService: EventService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    // Lire queryParams (ex: /sales/entry-itinerant?eventId=evt-comp-1)
    this.route.queryParams.subscribe((params) => {
      this.queryEventId = params['eventId'] || null;
      this.editSaleId = params['editSaleId'] || null;
      this.directNav = !!this.queryEventId;
    });

    this.loadProducts();
    this.loadPendingEvents();
  }

  // ═══════════════════════════════════════════════
  // CHARGEMENT DONNÉES
  // ═══════════════════════════════════════════════

  loadProducts(): void {
    this.productsService.getMyProducts().subscribe({
      next: (response: any) => {
        this.products = response.data || response;
        this.tryAutoAdvance();
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.log.error('Erreur chargement produits:', err);
        this.error = 'Impossible de charger les produits';
        this.cdr.markForCheck();
      },
    });
  }

  loadPendingEvents(): void {
    this.loading = true;
    this.dailySalesService.getPendingEvents().subscribe({
      next: (events) => {
        this.pendingEvents = events || [];
        this.loading = false;

        // Si eventId dans queryParams, pré-sélectionner sans avancer (on attend produits + prédictions)
        if (this.queryEventId) {
          const target = this.pendingEvents.find(
            (e) => e._id === this.queryEventId,
          );
          if (target) {
            this.selectedEvent = target;
            this.loadEventPredictions(target._id);
          } else if (this.editSaleId) {
            // L'event est terminé : il n'est plus dans pendingEvents
            // Charger depuis l'API événements directement
            this.eventService.getEvent(this.queryEventId).subscribe({
              next: (appEvent) => {
                // Mapper AppEvent → PendingEvent
                this.selectedEvent = {
                  _id: appEvent.id,
                  name: appEvent.name,
                  eventDate:
                    appEvent.eventDate instanceof Date
                      ? appEvent.eventDate.toISOString()
                      : String(appEvent.eventDate),
                  eventType: appEvent.eventType,
                  location: appEvent.location,
                  status: 'completed',
                };
                this.loadEventPredictions(appEvent.id);
                this.cdr.markForCheck();
              },
              error: () => {
                this.error = "Impossible de charger les détails de l'événement";
                this.cdr.markForCheck();
              },
            });
          }
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.log.error('Erreur chargement événements:', err);
        this.error = 'Impossible de charger les événements';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ═══════════════════════════════════════════════
  // ÉTAPE 1 — SÉLECTION ÉVÉNEMENT
  // ═══════════════════════════════════════════════

  selectEvent(event: PendingEvent): void {
    this.selectedEvent = event;
    this.loadEventPredictions(event._id);
  }

  loadEventPredictions(eventId: string): void {
    this.predictionsLoaded = false;
    this.predictions.clear();

    this.predictionsService.getEventPrediction(eventId).subscribe({
      next: (prediction: any) => {
        // Le backend retourne { products: [...], recommendedQuantity }
        // Le frontend attend predictedProducts[].predictedQuantity — on normalise ici
        const rawProducts: any[] =
          prediction.predictedProducts ?? prediction.products ?? [];
        rawProducts.forEach((product: any) => {
          this.predictions.set(product.productId?.toString(), {
            ...product,
            productId: product.productId?.toString(),
            predictedQuantity:
              product.predictedQuantity ?? product.recommendedQuantity ?? 0,
            confidenceLevel:
              product.confidenceLevel ?? product.confidenceScore ?? 0,
          });
        });
        this.predictionsLoaded = true;
        this.log.debug(
          '✅ Prédictions événement chargées:',
          this.predictions.size,
        );
        this.tryAutoAdvance();
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.log.error('⚠️ Erreur prédictions événement:', err);
        this.predictionsLoaded = true;
        this.tryAutoAdvance();
        this.cdr.markForCheck();
      },
    });
  }

  /** Avance automatiquement vers la saisie quand produits + prédictions sont prêts (nav directe) */
  private tryAutoAdvance(): void {
    if (
      this.directNav &&
      this.predictionsLoaded &&
      this.products.length > 0 &&
      this.selectedEvent &&
      this.step === 'select-event'
    ) {
      this.proceedToSalesEntry();
    }
  }

  getRecommendedQuantity(
    productId: string,
    productName?: string,
  ): number | null {
    // Lookup by ID (primary)
    const prediction = this.predictions.get(productId);
    if (prediction) return prediction.predictedQuantity;
    // Fallback: lookup by productName (handles stale cached recommendations with old IDs)
    if (productName) {
      for (const [, pred] of this.predictions) {
        if (pred.productName === productName) return pred.predictedQuantity;
      }
    }
    return null;
  }

  // ═══════════════════════════════════════════════
  // NAVIGATION ENTRE ÉTAPES
  // ═══════════════════════════════════════════════

  proceedToSalesEntry(): void {
    if (!this.selectedEvent) {
      this.error = 'Veuillez sélectionner un événement';
      return;
    }

    this.loading = true;
    this.error = '';

    // Mode correction : charger le DailySales existant
    if (this.editSaleId) {
      this.dailySalesService.getById(this.editSaleId).subscribe({
        next: (sale) => {
          this.loadExistingSale(sale);
          this.step = 'enter-sales';
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.initializeEmptySales();
          this.step = 'enter-sales';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
      return;
    }

    // Essayer de charger un brouillon existant
    this.dailySalesService
      .getDraft({
        saleDate: this.selectedEvent.eventDate,
        eventId: this.selectedEvent._id,
      })
      .subscribe({
        next: (draft) => {
          if (draft) {
            this.loadDraft(draft);
          } else {
            this.initializeEmptySales();
          }
          this.step = 'enter-sales';
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.initializeEmptySales();
          this.step = 'enter-sales';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  proceedToContext(): void {
    const validationError = this.dailySalesService.validateProducts(
      this.productSales,
    );
    if (validationError) {
      this.error = validationError;
      return;
    }
    this.error = '';
    this.step = 'context';
  }

  proceedToSummary(): void {
    this.computeAiFeedback();
    this.step = 'summary';
  }

  goBack(): void {
    if (this.step === 'enter-sales') this.step = 'select-event';
    else if (this.step === 'context') this.step = 'enter-sales';
    else if (this.step === 'summary') this.step = 'context';
  }

  // ═══════════════════════════════════════════════
  // ÉTAPE 2 — SAISIE PRODUITS (aligné avec fixed)
  // ═══════════════════════════════════════════════

  loadDraft(draft: any): void {
    this.productSales = draft.products || [];
    this.contextFlags = draft.contextFlags || this.contextFlags;
    this.notes = draft.notes || '';
    this.actualAttendance = draft.actualAttendance || '';
    this.actualWeather = draft.actualWeather || '';
    this.actualDuration = draft.actualDuration || 0;
    this.isDraft = true;
  }

  /** Pré-remplir depuis un DailySales existant (mode correction) */
  loadExistingSale(sale: any): void {
    this.productSales = (sale.products || [])
      .map((p: any) => {
        // productId peut être un objet Product populé (depuis getById avec populate)
        // ou un ObjectId string, ou null si produit supprimé
        let productId: string;
        if (p.productId && typeof p.productId === 'object') {
          productId = (p.productId._id || p.productId.id || '').toString();
        } else {
          productId = (p.productId || '').toString();
        }

        // Fallback : retrouver l'ID depuis le catalogue par nom si vide
        if (!productId) {
          const match = this.products.find(
            (prod) => prod.name === p.productName,
          );
          productId = ((match as any)?._id || match?.id || '').toString();
        }

        return {
          productId,
          productName: p.productName,
          recommended: p.quantityRecommended ?? null,
          prepared: p.quantityPrepared,
          sold: p.quantitySold,
          wasted: p.quantityWasted,
          hasRupture: p.hadRupture ?? false,
        };
      })
      .filter((p: any) => !!p.productId); // exclure produits supprimés du catalogue
    this.contextFlags = sale.contextFlags || this.contextFlags;
    this.notes = sale.actualContext?.notes || '';
    this.actualAttendance = sale.actualAttendance || '';
    this.actualWeather = sale.actualWeather || '';
    this.actualDuration = sale.actualDuration || 0;
    this.isDraft = false;
  }

  /**
   * Initialiser saisie vide — pré-rempli depuis prédictions IA
   * Réutilise la même logique que fixed (recommended → prepared)
   */
  initializeEmptySales(): void {
    this.productSales = this.products.map((product) => {
      const productId = ((product as any)._id || product.id || '').toString();
      const recommended = this.getRecommendedQuantity(productId, product.name);
      const defaultPrepared = recommended ?? 0;
      return {
        productId: productId,
        productName: product.name,
        recommended: recommended,
        prepared: defaultPrepared,
        sold: undefined as any,
        wasted: undefined as any,
        hasRupture: false,
      };
    });
  }

  getProductPrices(productId: string): {
    sellPrice: number | null;
    purchasePrice: number | null;
  } {
    const p = this.products.find(
      (pr) => ((pr as any)._id || pr.id || '').toString() === productId,
    );
    return {
      sellPrice: p?.sellPrice ?? null,
      purchasePrice: (p as any)?.purchasePrice ?? null,
    };
  }

  /**
   * Calculer gaspillage auto + validation sold <= prepared
   * (identique au fixed)
   */
  onPreparedOrSoldChange(product: ProductSale): void {
    if (product.sold != null && product.sold > product.prepared) {
      product.sold = product.prepared;
    }
    if (product.sold != null && product.prepared >= product.sold) {
      product.wasted = product.prepared - product.sold;
    }
  }

  /**
   * Tout vendu (identique au fixed)
   */
  setSoldToPrepared(product: ProductSale): void {
    product.sold = product.prepared;
    product.wasted = 0;
  }

  /**
   * Épuisé avec rupture (identique au fixed)
   */
  setExhausted(product: ProductSale): void {
    product.sold = product.prepared;
    product.wasted = 0;
    product.hasRupture = true;
  }

  /**
   * Définir restant en % (identique au fixed)
   */
  setRemaining(product: ProductSale, remainingPercent: number): void {
    const soldPercent = 100 - remainingPercent;
    product.sold = Math.round((product.prepared * soldPercent) / 100);
    product.wasted = product.prepared - product.sold;
    product.hasRupture = false;
  }

  // ═══════════════════════════════════════════════
  // BROUILLON & SOUMISSION
  // ═══════════════════════════════════════════════

  private buildPayload(isDraft: boolean): DailySalesPayload {
    return {
      merchantType: 'itinerant',
      source: 'manual',
      eventId: this.selectedEvent!._id,
      eventName: this.selectedEvent!.name,
      saleDate: this.selectedEvent!.eventDate,
      products: this.productSales
        .filter((p) => (p.prepared > 0 || (p.sold ?? 0) > 0) && !!p.productId)
        .map((p) => {
          const product = this.products.find(
            (prod) =>
              prod.id === p.productId || (prod as any)._id === p.productId,
          );
          const unitPrice = product?.sellPrice || 0;
          return {
            productId: p.productId,
            productName: p.productName,
            quantityRecommended: p.recommended || undefined,
            quantityPrepared: p.prepared,
            quantitySold: p.sold ?? 0,
            quantityWasted: p.wasted ?? 0,
            unitPrice: unitPrice,
            hasRupture: p.hasRupture,
          };
        }),
      contextFlags: this.contextFlags,
      notes: this.notes,
      actualAttendance: this.actualAttendance,
      actualWeather: this.actualWeather,
      actualDuration: this.actualDuration || undefined,
      isDraft,
    };
  }

  saveDraft(): void {
    if (!this.selectedEvent) return;
    this.loading = true;
    this.error = '';

    this.dailySalesService.saveDraft(this.buildPayload(true)).subscribe({
      next: () => {
        this.loading = false;
        this.successMessage = 'Brouillon sauvegardé avec succès';
        setTimeout(() => (this.successMessage = ''), 3000);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.error = err.error?.error || 'Erreur sauvegarde brouillon';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  submit(): void {
    if (!this.selectedEvent) return;
    this.loading = true;
    this.error = '';

    const payload = this.buildPayload(false);

    // Mode correction : PUT sur le document existant
    if (this.editSaleId) {
      this.dailySalesService.update(this.editSaleId, payload).subscribe({
        next: (sale) => {
          this.dailySalesService.validate(this.editSaleId!).subscribe({
            next: (validationResult) => {
              this.loading = false;
              this.submittedAccuracy =
                (sale as any).summary?.averageAccuracy || 0;
              this.learningUpdated = validationResult.learningUpdated;
              this.computeAiFeedback();
              this.successMessage = 'Ventes sauvegardées';
              this.cdr.markForCheck();
              setTimeout(
                () =>
                  this.router.navigate([
                    '/events',
                    this.selectedEvent!._id,
                    'rapport',
                  ]),
                2000,
              );
            },
            error: () => {
              this.loading = false;
              this.successMessage = 'Ventes sauvegardées';
              this.cdr.markForCheck();
              setTimeout(
                () =>
                  this.router.navigate([
                    '/events',
                    this.selectedEvent!._id,
                    'rapport',
                  ]),
                2000,
              );
            },
          });
        },
        error: (err) => {
          this.error = err.error?.error || 'Erreur correction ventes';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
      return;
    }

    this.dailySalesService.create(payload).subscribe({
      next: (sale) => {
        // Valider pour déclencher l'apprentissage
        this.dailySalesService.validate(sale._id).subscribe({
          next: (validationResult) => {
            this.loading = false;
            this.submittedAccuracy =
              sale.summary?.averageAccuracyVsRecommendation || 0;
            this.learningUpdated = validationResult.learningUpdated;
            this.computeAiFeedback();

            this.successMessage = 'Ventes sauvegardées';
            this.cdr.markForCheck();

            // Redirection après 2 secondes
            setTimeout(() => {
              this.router.navigate(['/dashboard']);
            }, 2000);
          },
          error: () => {
            this.loading = false;
            this.successMessage = 'Ventes sauvegardées';
            this.cdr.markForCheck();
            setTimeout(() => {
              this.router.navigate(['/dashboard']);
            }, 2000);
          },
        });
      },
      error: (err) => {
        this.error = err.error?.error || 'Erreur enregistrement ventes';
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  cancel(): void {
    if (
      confirm(
        'Êtes-vous sûr de vouloir annuler ? Les modifications non sauvegardées seront perdues.',
      )
    ) {
      this.router.navigate(['/dashboard']);
    }
  }

  // ═══════════════════════════════════════════════
  // CALCULS RÉSUMÉ (Step 4)
  // ═══════════════════════════════════════════════

  getTotalPrepared(): number {
    return this.productSales.reduce((acc, p) => acc + (p.prepared || 0), 0);
  }

  getTotalSold(): number {
    return this.productSales.reduce((acc, p) => acc + (p.sold ?? 0), 0);
  }

  getTotalWasted(): number {
    return this.productSales.reduce((acc, p) => acc + (p.wasted ?? 0), 0);
  }

  getTotalRuptures(): number {
    return this.productSales.filter((p) => p.hasRupture).length;
  }

  getTotalRevenue(): number {
    return this.productSales.reduce((acc, p) => {
      const prod = this.products.find((pr) => pr.id === p.productId);
      return acc + (p.sold ?? 0) * (prod?.sellPrice || 0);
    }, 0);
  }

  getTotalWasteValue(): number {
    return this.productSales.reduce((acc, p) => {
      const prod = this.products.find((pr) => pr.id === p.productId);
      return (
        acc + (p.wasted ?? 0) * (prod?.purchasePrice || prod?.sellPrice || 0)
      );
    }, 0);
  }

  getWasteRate(): number {
    const prepared = this.getTotalPrepared();
    if (prepared === 0) return 0;
    return Math.round((this.getTotalWasted() / prepared) * 100);
  }

  getSellThroughRate(): number {
    const prepared = this.getTotalPrepared();
    if (prepared === 0) return 0;
    return Math.round((this.getTotalSold() / prepared) * 100);
  }

  /**
   * Calcul de la précision par produit (vs recommandation)
   */
  getProductAccuracy(product: ProductSale): number | null {
    if (!product.recommended || product.recommended === 0) return null;
    if (product.sold == null) return null;
    const deviation = Math.abs(product.sold - product.recommended);
    return Math.round(Math.max(0, (1 - deviation / product.recommended) * 100));
  }

  /**
   * Précision moyenne globale
   */
  getAverageAccuracy(): number {
    const accuracies = this.productSales
      .map((p) => this.getProductAccuracy(p))
      .filter((a): a is number => a !== null);
    if (accuracies.length === 0) return 0;
    return Math.round(
      accuracies.reduce((sum, a) => sum + a, 0) / accuracies.length,
    );
  }

  /**
   * Feedback IA automatique basé sur les ventes
   */
  computeAiFeedback(): void {
    this.aiFeedbackMessages = [];
    const accuracy = this.getAverageAccuracy();
    const wasteRate = this.getWasteRate();
    const ruptures = this.getTotalRuptures();

    // Feedback précision globale
    if (accuracy >= 90) {
      this.aiFeedbackMessages.push({
        icon: '🎯',
        message:
          'Excellent ! Vos ventes correspondent très bien aux recommandations.',
        type: 'success',
      });
    } else if (accuracy >= 75) {
      this.aiFeedbackMessages.push({
        icon: '👍',
        message:
          "Bonne correspondance. L'IA ajustera légèrement ses prochaines recommandations.",
        type: 'info',
      });
    } else if (accuracy >= 50) {
      this.aiFeedbackMessages.push({
        icon: '🔧',
        message:
          "Écart notable. L'IA va recalibrer ses modèles pour mieux s'adapter.",
        type: 'warning',
      });
    } else {
      this.aiFeedbackMessages.push({
        icon: '📊',
        message:
          "Écart important. Plus vous saisissez de données, plus l'IA sera précise.",
        type: 'info',
      });
    }

    // Feedback gaspillage
    if (wasteRate > 25) {
      this.aiFeedbackMessages.push({
        icon: '♻️',
        message: `Taux de gaspillage élevé (${wasteRate}%). L'IA réduira les quantités recommandées pour les prochains événements similaires.`,
        type: 'warning',
      });
    } else if (wasteRate < 5 && ruptures === 0) {
      this.aiFeedbackMessages.push({
        icon: '💚',
        message:
          'Très peu de gaspillage et aucune rupture ! Préparation optimale.',
        type: 'success',
      });
    }

    // Feedback ruptures
    if (ruptures > 0) {
      this.aiFeedbackMessages.push({
        icon: '⚡',
        message: `${ruptures} rupture${ruptures > 1 ? 's' : ''} détectée${ruptures > 1 ? 's' : ''}. L'IA augmentera les recommandations pour ces produits.`,
        type: 'warning',
      });
    }

    // Feedback produits spécifiques
    const bestProduct = this.productSales
      .filter((p) => p.recommended && p.sold != null)
      .sort(
        (a, b) =>
          (this.getProductAccuracy(b) ?? 0) - (this.getProductAccuracy(a) ?? 0),
      )[0];

    if (bestProduct && (this.getProductAccuracy(bestProduct) ?? 0) >= 95) {
      this.aiFeedbackMessages.push({
        icon: '⭐',
        message: `"${bestProduct.productName}" : prédiction quasi-parfaite ! L'IA maîtrise bien ce produit.`,
        type: 'success',
      });
    }
  }

  getAccuracyColor(accuracy: number): string {
    if (accuracy >= 85) return 'text-green-600';
    if (accuracy >= 70) return 'text-yellow-600';
    return 'text-red-600';
  }

  getAccuracyBgColor(accuracy: number): string {
    if (accuracy >= 85) return 'bg-green-500';
    if (accuracy >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  }

  getFeedbackBg(type: string): string {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  }

  getContextFlagCount(): number {
    return Object.values(this.contextFlags).filter(Boolean).length;
  }

  getActiveContextFlags(): string[] {
    const labels: Record<string, string> = {
      ruptureStock: 'Rupture de stock',
      unusualAffluence: 'Affluence inhabituelle',
      weatherImpact: 'Météo impactante',
      localEvent: 'Événement local',
      technicalIssue: 'Problème technique',
      differentLocation: 'Emplacement différent',
    };
    return Object.entries(this.contextFlags)
      .filter(([, v]) => v)
      .map(([k]) => labels[k] || k);
  }

  get currentStepIndex(): number {
    const steps = ['select-event', 'enter-sales', 'context', 'summary'];
    return steps.indexOf(this.step) + 1;
  }
}
